import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import CallbackPhoto from '../../assets/images/hardware-first/ultra-callback.webp';
import StarlinkPhoto from '../../assets/images/hardware-first/ultra-starlink.webp';
import { ReactComponent as IconLightning } from '../../assets/images/icons/icon_lightning.svg';
import { ReactComponent as IconLocation } from '../../assets/images/icons/icon_location.svg';
import { ReactComponent as IconSatellite } from '../../assets/images/icons/icon_satellite.svg';
import { ReactComponent as IconShield } from '../../assets/images/icons/compare_shield.svg';
import { ReactComponent as IconVibration } from '../../assets/images/icons/icon_vibration.svg';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import sheet from './HardwareFirstIntelligenceModal.module.scss';
import styles from './HardwareFirstUltraFeatureModal.module.scss';

export type UltraFeature = 'starlink' | 'callback';

// the ultra page's own photos and copy (marketingsite ultra slides), on the fi intelligence sheet's frame
const FEATURES = {
  starlink: {
    photo: StarlinkPhoto,
    kickerKey: 'pdp_v2_starlink_kicker',
    titleKey: 'pdp_v2_starlink_title',
    introKey: 'pdp_v2_starlink_intro',
    rows: [
      { Icon: IconLocation, titleKey: 'pdp_v2_starlink_remote_title', bodyKey: 'pdp_v2_starlink_remote_body' },
      { Icon: IconSatellite, titleKey: 'pdp_v2_starlink_global_title', bodyKey: 'pdp_v2_starlink_global_body' },
      { Icon: IconLightning, titleKey: 'pdp_v2_starlink_fast_title', bodyKey: 'pdp_v2_starlink_fast_body' },
    ],
    footKey: 'ultra_satellite_disclaimer',
  },
  callback: {
    photo: CallbackPhoto,
    kickerKey: 'pdp_v2_callback_kicker',
    titleKey: 'pdp_v2_callback_title',
    introKey: 'pdp_v2_callback_intro',
    rows: [
      { Icon: IconVibration, titleKey: 'pdp_v2_callback_manual_title', bodyKey: 'pdp_v2_callback_manual_body' },
      { Icon: IconLocation, titleKey: 'pdp_v2_callback_zones_title', bodyKey: 'pdp_v2_callback_zones_body' },
      { Icon: IconShield, titleKey: 'pdp_v2_callback_humane_title', bodyKey: 'pdp_v2_callback_humane_body' },
    ],
    footKey: undefined,
  },
};

/** The sheets behind the solo ultra page's satellite and recall tiles: a photo, an intro and three rows. */
export default function HardwareFirstUltraFeatureModal({
  feature,
  trigger,
}: {
  feature: UltraFeature;
  trigger: JSX.Element;
}) {
  const { t } = useTranslation();
  const { photo, kickerKey, titleKey, introKey, rows, footKey } = FEATURES[feature];
  return (
    <SimpleDismissableModal trigger={trigger} containerClassName={sheet.modal} closeIconClassName={sheet.closeIcon}>
      {(close) => (
        <div className={sheet.container}>
          <div className={sheet.scroll}>
            <div className={classNames(styles.hero, styles[feature])} style={{ backgroundImage: `url(${photo})` }}>
              <div className={styles.kicker}>{t(kickerKey)}</div>
              <h2 className={styles.title}>{t(titleKey)}</h2>
            </div>
            <div className={sheet.body}>
              <p className={styles.intro}>{t(introKey)}</p>
              <ul className={sheet.rows}>
                {rows.map(({ Icon, titleKey: rowTitle, bodyKey }) => (
                  <li key={rowTitle} className={sheet.row}>
                    <span className={sheet.rowIcon}>
                      <Icon />
                    </span>
                    <span>
                      <span className={sheet.rowTitle}>{t(rowTitle)}</span>
                      <span className={sheet.rowBody}>{t(bodyKey)}</span>
                    </span>
                  </li>
                ))}
              </ul>
              {footKey && <p className={sheet.foot}>{t(footKey)}</p>}
            </div>
          </div>
          <div className={sheet.ctaBar}>
            <button type="button" className={sheet.cta} onClick={close}>
              {t('got_it')}
            </button>
          </div>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
